import { useCallback, useEffect, useRef } from 'react';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* One shared frame loop reveals elements as they enter the viewport.
   Polling keeps this working where observers or scroll events are throttled,
   and it stops itself once everything has been revealed. */
const pending = new Set();
let loopHandle = 0;

const isInView = node => {
  const rect = node.getBoundingClientRect();
  const viewport = window.innerHeight || document.documentElement.clientHeight;
  if (rect.bottom <= 0) return true; // already scrolled past, no reason to keep it hidden
  return rect.top < viewport - 60 && rect.bottom > 0;
};

function runLoop() {
  loopHandle = 0;
  pending.forEach(node => {
    if (!node.isConnected) { pending.delete(node); return; }
    if (!isInView(node)) return;
    node.classList.add('is-revealed');
    pending.delete(node);
  });
  if (pending.size) loopHandle = requestAnimationFrame(runLoop);
}

function watch(node) {
  pending.add(node);
  if (!loopHandle) loopHandle = requestAnimationFrame(runLoop);
}

export function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (prefersReducedMotion()) {
      node.classList.add('is-revealed');
      return undefined;
    }

    watch(node);
    return () => pending.delete(node);
  }, []);

  return ref;
}

export function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`reveal ${className}`.trim()} style={delay ? { '--reveal-delay': `${delay}ms` } : undefined} {...rest}>
      {children}
    </Tag>
  );
}

/* Pointer-driven 3D tilt. Falls back to a static card when motion is reduced. */
export function TiltCard({ as: Tag = 'div', className = '', intensity = 7, lift = 6, children, ...rest }) {
  const ref = useRef(null);
  const frame = useRef(0);

  const handleMove = useCallback(event => {
    const node = ref.current;
    if (!node || prefersReducedMotion()) return;
    const rect = node.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      node.style.setProperty('--tilt-x', `${(-y * intensity).toFixed(2)}deg`);
      node.style.setProperty('--tilt-y', `${(x * intensity).toFixed(2)}deg`);
      node.style.setProperty('--tilt-lift', `${lift}px`);
      node.style.setProperty('--glare-x', `${((x + 0.5) * 100).toFixed(1)}%`);
      node.style.setProperty('--glare-y', `${((y + 0.5) * 100).toFixed(1)}%`);
    });
  }, [intensity, lift]);

  const handleLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    cancelAnimationFrame(frame.current);
    node.style.setProperty('--tilt-x', '0deg');
    node.style.setProperty('--tilt-y', '0deg');
    node.style.setProperty('--tilt-lift', '0px');
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return (
    <Tag ref={ref} className={`tilt ${className}`.trim()} onPointerMove={handleMove} onPointerLeave={handleLeave} {...rest}>
      {children}
    </Tag>
  );
}

/* Shared pointer position for parallax scenes, expressed as CSS custom properties. */
export function usePointerScene() {
  const ref = useRef(null);
  const frame = useRef(0);

  const handleMove = useCallback(event => {
    const node = ref.current;
    if (!node || prefersReducedMotion()) return;
    const rect = node.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      node.style.setProperty('--pointer-x', x.toFixed(3));
      node.style.setProperty('--pointer-y', y.toFixed(3));
    });
  }, []);

  const handleLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    cancelAnimationFrame(frame.current);
    node.style.setProperty('--pointer-x', '0');
    node.style.setProperty('--pointer-y', '0');
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return { ref, onPointerMove: handleMove, onPointerLeave: handleLeave };
}

/* Toggles a class on the element once the page scrolls past a threshold. */
export function useScrolled(threshold = 24) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      node.classList.toggle('is-scrolled', window.scrollY > threshold);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(frame); };
  }, [threshold]);

  return ref;
}
